import React, { useMemo } from 'react';
import Box from '@mui/material/Box';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import PropertyCard from './ui/PropertyCard';
import DataItem from './ui/DataItem';
import { formatValue } from '../../utils/formatters/valueUtils';
import { formatNumber } from '../../utils/formatters/numberUtils';
import { formatDateBr, formatDateTimeBr } from '../../utils/formatters/dateUtils';
import { dedupeBySerializedValue } from '../../utils/propertyUtils';

const formatCurrencyBr = (value) => {
  if (value === null || value === undefined || value === '') {
    return '--';
  }
  const number = Number(value);
  if (Number.isNaN(number)) return '--';
  return number.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatPercent = (value) => {
  if (value === null || value === undefined || value === '') {
    return '--';
  }
  const number = Number(value);
  if (Number.isNaN(number)) return '--';
  return `${number.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;
};

const formatCountLabel = (total, unique) => {
  if (total <= 0) return '0 registros';
  if (total === unique) return `${unique} registro(s)`;
  return `${total} registro(s) (${unique} único(s))`;
};

const formatDocument = (value) => {
  if (!value) return '--';
  const digits = String(value).replace(/\D/g, '');
  if (!digits) return value;
  if (digits.length === 11) {
    return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  }
  if (digits.length === 14) {
    return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  }
  return value;
};

const getStatusChipColor = (status) => {
  if (!status) return 'default';
  const normalized = status.toLowerCase();
  if (
    normalized.includes('quitado') ||
    normalized.includes('válida') ||
    normalized.includes('valida')
  ) {
    return 'success';
  }
  if (normalized.includes('pendente') || normalized.includes('vencido')) {
    return 'warning';
  }
  return 'default';
};

const CcirTab = ({ propertyData }) => {
  const ccirRecord = useMemo(() => {
    const props = propertyData?.properties;
    if (!props || typeof props !== 'object') return null;
    const ccirKey = Object.keys(props).find((key) => {
      const lower = key.toLowerCase();
      return lower === 'serpro_ccir' || lower === 'ccir';
    });
    if (!ccirKey) return null;
    const raw = props[ccirKey];
    if (Array.isArray(raw)) {
      return raw.find((item) => !!item) || null;
    }
    if (raw && typeof raw === 'object') {
      return raw;
    }
    return null;
  }, [propertyData]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const areasRegistradasRaw = Array.isArray(ccirRecord?.areas_registradas)
    ? ccirRecord.areas_registradas
    : [];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const titularesRaw = Array.isArray(ccirRecord?.titulares) ? ccirRecord.titulares : [];

  const areasRegistradas = useMemo(
    () => dedupeBySerializedValue(areasRegistradasRaw),
    [areasRegistradasRaw],
  );

  const titulares = useMemo(() => dedupeBySerializedValue(titularesRaw), [titularesRaw]);

  const dadosUltimoCcir = ccirRecord?.dados_ultimo_ccir || null;

  if (!ccirRecord) {
    return (
      <div className="p-4 bg-gray-50 rounded border border-gray-200 text-gray-600">
        Nenhum dado de CCIR disponível para esta propriedade.
      </div>
    );
  }

  return (
    <Box className="space-y-4">
      <PropertyCard
        title="Cadastro de Imóvel Rural (CCIR)"
        subtitle="Informações consolidadas SERPRO"
        color="green"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          <DataItem
            label="Código INCRA"
            value={formatValue(ccirRecord.codigo_imovel_incra)}
            isCopyable
            highlight
          />
          <DataItem label="Denominação" value={formatValue(ccirRecord.denominacao)} />
          <DataItem
            label="Classificação fundiária"
            value={formatValue(ccirRecord.classificacao_fundiaria)}
          />
          <DataItem label="Município sede" value={formatValue(ccirRecord.municipio_sede)} />
          <DataItem label="UF sede" value={formatValue(ccirRecord.uf_sede)} />
          <DataItem
            label="Área total"
            value={`${formatNumber(ccirRecord.area_total || 0, 2)} ha`}
          />
          <DataItem
            label="Indicações de localização"
            value={formatValue(ccirRecord.indicacoes_localizacao)}
          />
          <DataItem
            label="Data processamento última declaração"
            value={formatDateTimeBr(ccirRecord.data_processamento_ultima_declaracao) || '--'}
          />
        </div>
      </PropertyCard>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <Typography variant="subtitle1" component="h4" sx={{ fontWeight: 600 }}>
              Estrutura fundiária
            </Typography>
            {ccirRecord.classificacao_fundiaria && (
              <Chip size="small" color="info" label={ccirRecord.classificacao_fundiaria} />
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <DataItem
              label="Área certificada"
              value={`${formatNumber(ccirRecord.area_certificada || 0, 2)} ha`}
            />
            <DataItem
              label="Total área registrada"
              value={`${formatNumber(ccirRecord.total_area_registrada || 0, 2)} ha`}
            />
            <DataItem
              label="Total área posse (justo título)"
              value={`${formatNumber(ccirRecord.total_area_posse_justo_titulo || 0, 2)} ha`}
            />
            <DataItem
              label="Total área posse (simples ocupação)"
              value={`${formatNumber(ccirRecord.total_area_posse_simples_ocupacao || 0, 2)} ha`}
            />
            <DataItem
              label="Área medida"
              value={`${formatNumber(ccirRecord.area_medida || 0, 2)} ha`}
            />
            <DataItem
              label="Total de pessoas relacionadas"
              value={formatValue(ccirRecord.total_pessoas_relacionadas_imovel)}
            />
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
          <Typography variant="subtitle1" component="h4" sx={{ fontWeight: 600, mb: 2 }}>
            Módulos e parâmetros
          </Typography>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <DataItem
              label="Área módulo rural"
              value={`${formatNumber(ccirRecord.area_modulo_rural || 0, 4)} ha`}
            />
            <DataItem
              label="Nº módulos rurais"
              value={formatNumber(ccirRecord.numero_modulos_rurais || 0, 2)}
            />
            <DataItem
              label="Área módulo fiscal"
              value={`${formatNumber(ccirRecord.area_modulo_fiscal || 0, 4)} ha`}
            />
            <DataItem
              label="Nº módulos fiscais"
              value={formatNumber(ccirRecord.numero_modulos_fiscais || 0, 2)}
            />
            <DataItem
              label="Fração mínima de parcelamento"
              value={`${formatNumber(ccirRecord.fracao_minima_parcelamento || 0, 2)} ha`}
            />
          </div>
        </div>
      </div>

      <Accordion disableGutters>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%',
              alignItems: 'center',
            }}
          >
            <Typography sx={{ fontWeight: 600 }}>Áreas registradas</Typography>
            <Typography variant="caption" color="text.secondary">
              {formatCountLabel(areasRegistradasRaw.length, areasRegistradas.length)}
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {areasRegistradas.length === 0 ? (
            <div className="text-gray-500">Sem dados disponíveis.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500">
                    <th className="pb-2 pr-4">Data registro</th>
                    <th className="pb-2 pr-4">Cartório (UF / Município)</th>
                    <th className="pb-2 pr-4">Matrícula / Registro</th>
                    <th className="pb-2 pr-4">Livro / Ficha</th>
                    <th className="pb-2">Área (ha)</th>
                  </tr>
                </thead>
                <tbody>
                  {areasRegistradas.map((area, index) => (
                    <tr key={`area-${index}`} className="border-t border-gray-100">
                      <td className="py-2 pr-4">{formatDateBr(area?.data_registro) || '--'}</td>
                      <td className="py-2 pr-4">
                        {[formatValue(area?.uf_cartorio), formatValue(area?.municipio_cartorio)]
                          .filter((item) => item && item !== '--')
                          .join(' / ') || '--'}
                      </td>
                      <td className="py-2 pr-4">
                        <div className="flex flex-col">
                          <span>{formatValue(area?.matricula_ou_transcricao)}</span>
                          <span className="text-xs text-gray-500">
                            {formatValue(area?.registro)}
                          </span>
                        </div>
                      </td>
                      <td className="py-2 pr-4">{formatValue(area?.livro_ou_ficha)}</td>
                      <td className="py-2">{formatNumber(area?.area || 0, 2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </AccordionDetails>
      </Accordion>

      <Accordion disableGutters>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%',
              alignItems: 'center',
            }}
          >
            <Typography sx={{ fontWeight: 600 }}>Titulares</Typography>
            <Typography variant="caption" color="text.secondary">
              {formatCountLabel(titularesRaw.length, titulares.length)}
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {titulares.length === 0 ? (
            <div className="text-gray-500">Sem dados disponíveis.</div>
          ) : (
            <div className="space-y-3">
              {titulares.map((titular, index) => (
                <div
                  key={`titular-${index}`}
                  className="bg-white border border-gray-100 rounded-lg p-3 shadow-sm"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    <DataItem label="Nome" value={formatValue(titular?.nome_titular)} />
                    <DataItem label="CPF/CNPJ" value={formatDocument(titular?.cpf_cnpj)} />
                    <DataItem label="Declarante" value={formatValue(titular?.declarante)} />
                    <DataItem
                      label="Condição de titularidade"
                      value={formatValue(titular?.condicao_titularidade)}
                    />
                    <DataItem
                      label="Percentual de detenção"
                      value={formatPercent(titular?.percentual_detencao)}
                    />
                    <DataItem label="Nacionalidade" value={formatValue(titular?.nacionalidade)} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </AccordionDetails>
      </Accordion>

      <Accordion disableGutters>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%',
              alignItems: 'center',
            }}
          >
            <Typography sx={{ fontWeight: 600 }}>Dados do último CCIR</Typography>
            {dadosUltimoCcir?.situacao_ccir && (
              <Chip
                size="small"
                color={getStatusChipColor(dadosUltimoCcir.situacao_ccir)}
                label={dadosUltimoCcir.situacao_ccir}
              />
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {dadosUltimoCcir ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <DataItem
                label="Data de lançamento"
                value={formatDateBr(dadosUltimoCcir.data_lancamento) || '--'}
              />
              <DataItem label="Número CCIR" value={formatValue(dadosUltimoCcir.numero_ccir)} />
              <DataItem
                label="Número de autenticidade"
                value={formatValue(dadosUltimoCcir.numero_autenticidade_ccir)}
              />
              <DataItem
                label="Data de geração"
                value={formatDateTimeBr(dadosUltimoCcir.data_geracao_ccir) || '--'}
              />
              <DataItem
                label="Data de vencimento"
                value={formatDateBr(dadosUltimoCcir.data_vencimento_ccir) || '--'}
              />
              <DataItem
                label="Débitos anteriores"
                value={formatCurrencyBr(dadosUltimoCcir.debitos_anteriores)}
              />
              <DataItem
                label="Taxa de serviços"
                value={formatCurrencyBr(dadosUltimoCcir.taxa_servicos_cadastrais)}
              />
              <DataItem
                label="Valor cobrado"
                value={formatCurrencyBr(dadosUltimoCcir.valor_cobrado)}
              />
              <DataItem label="Multa" value={formatCurrencyBr(dadosUltimoCcir.multa)} />
              <DataItem label="Juros" value={formatCurrencyBr(dadosUltimoCcir.juros)} />
              <DataItem label="Valor total" value={formatCurrencyBr(dadosUltimoCcir.valor_total)} />
            </div>
          ) : (
            <div className="text-gray-500">Sem dados disponíveis.</div>
          )}
        </AccordionDetails>
      </Accordion>

      <Divider />
    </Box>
  );
};

export default CcirTab;
